import React from 'react';
import { theme } from '../../styles/theme';
import { compareVersions } from '../../utils/version';
import type { AudienceResponse, SegmentFilters as SegmentFiltersValue } from '../../types/push';

interface Props {
  value: SegmentFiltersValue;
  onChange: (value: SegmentFiltersValue) => void;
  /** Versões em uso no segmento (sem o próprio filtro de versão). */
  appVersions: AudienceResponse['appVersions'];
}

/** Primeira versão do app que informa a própria versão: "anterior a ela" = quem precisa atualizar. */
const FIRST_REPORTING_VERSION = '1.12.0';

const LAST_SEEN_OPTIONS = [
  { value: '', label: 'Qualquer data' },
  { value: 'within:7', label: 'Acessou nos últimos 7 dias' },
  { value: 'within:30', label: 'Acessou nos últimos 30 dias' },
  { value: 'over:30', label: 'Sem acesso há mais de 30 dias' },
  { value: 'over:60', label: 'Sem acesso há mais de 60 dias' },
  { value: 'over:90', label: 'Sem acesso há mais de 90 dias' },
];

export const EMPTY_SEGMENT: SegmentFiltersValue = { platform: null, plan: null, lastSeen: null, appVersion: null };

const encodeLastSeen = (lastSeen: SegmentFiltersValue['lastSeen']) => (lastSeen ? `${lastSeen.op}:${lastSeen.days}` : '');

const decodeLastSeen = (raw: string): SegmentFiltersValue['lastSeen'] => {
  if (!raw) return null;
  const [op, days] = raw.split(':');
  return { op: op as 'within' | 'over', days: Number(days) };
};

const encodeVersion = (appVersion: SegmentFiltersValue['appVersion']) =>
  appVersion ? `${appVersion.op}:${appVersion.version}` : '';

/** "Anterior a" inclui as versões desconhecidas (app que ainda não reporta); "X ou posterior" não. */
const decodeVersion = (raw: string): SegmentFiltersValue['appVersion'] => {
  if (!raw) return null;
  const [op, version] = raw.split(':');
  return { op: op as 'lt' | 'gte', version, includeUnknown: op === 'lt' };
};

/** Filtros do segmento (plataforma, plano, último acesso, versão do app), combinados com "e". */
const SegmentFilters: React.FC<Props> = ({ value, onChange, appVersions }) => {
  const update = (changes: Partial<SegmentFiltersValue>) => onChange({ ...value, ...changes });
  const active = Object.values(value).some(item => item !== null);

  const versions = Array.from(
    new Set([
      FIRST_REPORTING_VERSION,
      ...appVersions.map(item => item.version).filter((version): version is string => !!version),
    ])
  ).sort((a, b) => compareVersions(b, a));

  return (
    <div style={styles.row}>
      <span style={styles.label}>Segmento:</span>

      <select
        style={styles.select}
        value={value.platform || ''}
        onChange={(e) => update({ platform: (e.target.value || null) as SegmentFiltersValue['platform'] })}
      >
        <option value="">Todas as plataformas</option>
        <option value="android">Android</option>
        <option value="ios">iOS</option>
      </select>

      <select
        style={styles.select}
        value={value.plan || ''}
        onChange={(e) => update({ plan: (e.target.value || null) as SegmentFiltersValue['plan'] })}
      >
        <option value="">Qualquer plano</option>
        <option value="active">Plano ativo</option>
        <option value="expired">Plano vencido</option>
      </select>

      <select
        style={styles.select}
        value={encodeLastSeen(value.lastSeen)}
        onChange={(e) => update({ lastSeen: decodeLastSeen(e.target.value) })}
        title="Último acesso = a última vez que o app chegou na tela inicial, em qualquer aparelho do negócio"
      >
        {LAST_SEEN_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>

      <select
        style={styles.select}
        value={encodeVersion(value.appVersion)}
        onChange={(e) => update({ appVersion: decodeVersion(e.target.value) })}
        title="O app só informa a versão a partir da 1.12. Antes disso a versão é desconhecida."
      >
        <option value="">Qualquer versão do app</option>
        {versions.map(version => (
          <React.Fragment key={version}>
            <option value={`lt:${version}`}>Anterior à {version} (inclui desconhecidas)</option>
            <option value={`gte:${version}`}>{version} ou posterior</option>
          </React.Fragment>
        ))}
      </select>

      {active && (
        <button style={styles.clearButton} onClick={() => onChange(EMPTY_SEGMENT)}>
          Limpar
        </button>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: theme.colors.text,
    marginRight: '4px',
  },
  select: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    background: 'white',
  },
  clearButton: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: theme.colors.primary,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

export default SegmentFilters;
